import type { OrderReadiness } from 'src/typings';
import { orderSupplierReceipt } from './update/order-warehouse-allocation.util';

interface ReadinessLine {
  required: number;
  reserved?: number;
  fulfilled?: number;
  transfer_pending?: number;
  transfer_reserved?: number;
  transfer_in_transit?: number;
  allocations?: Array<{
    provider_id?: number;
    status: string;
    quantity: number;
    received_quantity?: number;
    physically_received_quantity?: number;
    physical_unreconciled_quantity?: number;
    expected_at?: string | Date | null;
  }>;
}

const EPSILON = 0.0001;
const quantity = (value: unknown) => Number.isFinite(Number(value)) ? Math.max(Number(value), 0) : 0;

/** A date promises readiness of the whole remaining order, never just one supplied row. */
export function calculateOrderReadiness(lines: ReadinessLine[]): OrderReadiness {
  if (!lines.length) return { status: 'EMPTY', expected_at: null };
  let unsourced = false, transferPending = false, dateMissing = false;
  const timestamps: number[] = [];
  for (const line of lines) {
    const remaining = Math.max(quantity(line.required) - quantity(line.fulfilled) - quantity(line.reserved), 0);
    if (remaining <= EPSILON) continue;
    const transfer = quantity(line.transfer_pending ??
      quantity(line.transfer_reserved) + quantity(line.transfer_in_transit));
    const sources = (line.allocations || []).filter(source =>
      Number(source.provider_id) > 0 &&
      ['PLANNED', 'ORDERED', 'PARTIALLY_RECEIVED'].includes(source.status) &&
      orderSupplierReceipt(source).open > EPSILON);
    const supplied = sources.reduce((sum, source) =>
      sum + orderSupplierReceipt(source).open, 0);
    if (remaining - supplied - transfer > EPSILON) unsourced = true;
    if (transfer > EPSILON) transferPending = true;
    for (const source of sources) {
      const timestamp = source.expected_at ? new Date(source.expected_at).getTime() : NaN;
      if (!Number.isFinite(timestamp)) dateMissing = true;
      else timestamps.push(timestamp);
    }
  }
  const status: OrderReadiness['status'] = unsourced ? 'UNSOURCED' : transferPending ? 'TRANSFER_PENDING' :
    dateMissing ? 'DATE_MISSING' : timestamps.length ? 'DATED' : 'IN_STOCK';
  return { status, expected_at: status === 'DATED' ? new Date(Math.max(...timestamps)).toISOString() : null };
}
